import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Notifications,
  WhatsApp,
  Email,
  Payment,
  Event,
  Warning,
  Diamond,
  Send,
  Settings,
  Phone,
  Message,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import PageHeader from '../components/PageHeader';
import { ACCENTS } from '../theme/accents';
import { useDistribution } from '../hooks/useMandalName';
import { useFeatures } from '../hooks/useFeatures';

const NotificationSettings = () => {
  const { sentence } = useDistribution();
  const { features } = useFeatures();
  const [preferences, setPreferences] = useState({
    whatsapp: true,
    email: true,
    payments: true,
    meetings: true,
    penalties: true,
    distributions: true
  });
  const [loading, setLoading] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [phone, setPhone] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchSettings();
    checkAdminStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/notifications/settings');
      setPreferences(response.data.preferences);
    } catch (error) {
      toast.error('Failed to load notification settings');
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setIsAdmin(response.data.user.is_admin);
      setPhone(response.data.user.phone || '');
    } catch (error) {
      console.error('Failed to check admin status');
    }
  };

  const handlePreferenceChange = (key) => (event) => {
    setPreferences(prev => ({
      ...prev,
      [key]: event.target.checked
    }));
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await axios.put('/api/notifications/settings', { preferences });
      toast.success('Notification settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      const payload = {};
      if (testPhone) payload.phone = testPhone;
      if (testMessage) payload.message = testMessage;

      const response = await axios.post('/api/notifications/test', payload);
      toast.success(`Test notification sent! Message ID: ${response.data.messageId}`);
      setTestDialogOpen(false);
      setTestMessage('');
      setTestPhone('');
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const notificationTypes = [
    {
      key: 'payments',
      title: 'Payment Notifications',
      description: 'Payment reminders, confirmations, and due date alerts',
      icon: <Payment color="primary" />
    },
    {
      key: 'meetings',
      title: 'Meeting Reminders',
      description: 'Upcoming meeting notifications and agenda updates',
      icon: <Event color="primary" />
    },
    {
      key: 'penalties',
      title: 'Penalty Alerts',
      description: 'Late payment penalties and fine notifications',
      icon: <Warning color="error" />
    },
    {
      key: 'distributions',
      title: 'Distribution Updates',
      description: `${sentence} and selection notifications`,
      icon: <Diamond color="warning" />
    }
  ]
    // A group that does no draw is not asked about draw messages
    .filter((type) => type.key !== 'distributions' || features.featureSilverDistribution);
  const hiddenKeys = features.featureSilverDistribution ? [] : ['distributions'];

  return (
    <Box>
      <PageHeader
        eyebrow="Preferences"
        accent={ACCENTS.teal}
        title="Notification Settings"
        subtitle="Choose what the group messages you about, and on which channel."
      />

      <Grid container spacing={3}>
        {/* Main Settings */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Communication Channels
            </Typography>
            
            <FormGroup sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.whatsapp}
                    onChange={handlePreferenceChange('whatsapp')}
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <WhatsApp sx={{ mr: 1, color: '#25D366' }} />
                    <Box>
                      <Typography variant="body1">WhatsApp Notifications</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Receive instant notifications via WhatsApp
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.email}
                    onChange={handlePreferenceChange('email')}
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <Email sx={{ mr: 1, color: '#1976d2' }} />
                    <Box>
                      <Typography variant="body1">Email Notifications</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Receive detailed notifications via email (Coming Soon)
                      </Typography>
                    </Box>
                  </Box>
                }
                disabled={true}
              />
            </FormGroup>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Notification Types
            </Typography>

            <List>
              {notificationTypes.map((type) => (
                <ListItem key={type.key} sx={{ px: 0 }}>
                  <ListItemIcon>
                    {type.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={type.title}
                    secondary={type.description}
                  />
                  <Switch
                    checked={preferences[type.key]}
                    onChange={handlePreferenceChange(type.key)}
                    disabled={!preferences.whatsapp && !preferences.email}
                  />
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={saveSettings}
                loading={loading}
                startIcon={<Settings />}
              >
                Save Settings
              </Button>

              {isAdmin && (
                <Button
                  variant="outlined"
                  onClick={() => setTestDialogOpen(true)}
                  startIcon={<Send />}
                >
                  Test Notification
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Delivery status — what is actually true about these messages */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsApp fontSize="small" />
                Delivery
              </Typography>

              {/* A member with WhatsApp switched on but no number saved gets
                  nothing at all, silently — so say so rather than showing a
                  reassuring green tick. */}
              {preferences.whatsapp && !phone ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No phone number is saved on your profile, so these notifications
                  cannot reach you. Add one under My Profile → Edit Profile.
                </Alert>
              ) : preferences.whatsapp ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Messages will be sent to <strong>{phone}</strong>.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  WhatsApp notifications are switched off. You will still see
                  everything in the app.
                </Alert>
              )}

              {preferences.whatsapp && (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    You will be messaged about:
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    {Object.entries(preferences)
                      .filter(([key, value]) => value && key !== 'whatsapp' && key !== 'email' && !hiddenKeys.includes(key))
                      .map(([key]) => (
                        <Chip
                          key={key}
                          label={key.charAt(0).toUpperCase() + key.slice(1)}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    {!Object.entries(preferences).some(([key, value]) => value && key !== 'whatsapp' && key !== 'email' && !hiddenKeys.includes(key)) && (
                      <Typography variant="body2" color="text.secondary">
                        Nothing — every category below is switched off.
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary">
                Messages are sent through an external WhatsApp provider, so treat
                them as you would any ordinary message — the group's records in
                this app remain the source of truth.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Test Notification Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Message sx={{ mr: 1 }} />
          Send Test Notification
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Send a test WhatsApp message to verify the notification system is working.
          </Alert>
          
          <TextField
            fullWidth
            label="Phone Number (Optional)"
            placeholder="917984821374"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            helperText="Leave empty to send to your registered number"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Custom Message (Optional)"
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            helperText="Leave empty to send default test message"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendTestNotification} startIcon={<Send />}>
            Send Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettings;